import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks/useAuth';
import { usePermissions, Panel } from '../hooks/usePermissions';
import { execApi } from '../services/api';
import AccessDenied from './AccessDenied';
import './Hub.css';

// The front door (Ohad 2026-08-12). Everyone lands here and sees exactly the boards
// they hold — nothing else. It replaces the old behaviour where "/" WAS the patients
// dashboard, which meant a person without that one grant hit a wall on arrival even
// though other boards were open to them.
//
// The card list comes from the backend's resolved grants, so what is drawn here and
// what the route guards allow can never disagree.
//
// Cards are draggable (Ohad 2026-08-15): drag one onto another to reorder, and the
// order is saved per USER on the server, so it follows them to any browser. The
// sidebar reads the same sorted list. Presentation only — the backend applies the
// order to the granted set and never lets it add or hide a board.

// Per-dashboard face. Kept next to the sidebar's vocabulary on purpose — the same
// board should look like itself in both places. Anything without an entry still
// renders, with a neutral mark, rather than being dropped.
const FACE: Record<string, { icon: string; tone: string }> = {
  exec: { icon: '◊', tone: 'indigo' },
  financial: { icon: '₪', tone: 'emerald' },
  patients: { icon: '□', tone: 'sky' },
  patient_base: { icon: '▣', tone: 'sky' },
  staff: { icon: '▤', tone: 'violet' },
  meetings: { icon: '◷', tone: 'amber' },
  meetings_list: { icon: '▦', tone: 'amber' },
  satisfaction: { icon: '◈', tone: 'rose' },
  wellness: { icon: '♡', tone: 'rose' },
  forecast: { icon: '◉', tone: 'indigo' },
  salary: { icon: '₪', tone: 'emerald' },
  billing: { icon: '⚖', tone: 'emerald' },
  oncall: { icon: '☎', tone: 'violet' },
  reports: { icon: '✉', tone: 'indigo' },
  training: { icon: '🎓', tone: 'violet' },
  ask: { icon: '🧠', tone: 'slate' },
  admin: { icon: '◎', tone: 'slate' },
  admin_data: { icon: '▣', tone: 'slate' },
};

// The two admin screens are ROLE-gated, not granted dashboards, so they never
// arrive in `panels` — appended here for admins (Ohad 2026-08-15) so the front
// door really lists everything they can open. Pinned after the ordered cards
// and excluded from dragging: they are not part of the saved order.
const ADMIN_CARDS: Panel[] = [
  { id: 'admin', name: 'ניהול משתמשים', route: '/admin', level: 'edit',
    purpose: 'הקמת משתמשים, תפקידים והרשאות פר-דשבורד.' },
  { id: 'admin_data', name: 'ניהול דשבורדים ונתונים', route: '/admin/data', level: 'edit',
    purpose: 'קטלוג הנתונים, יומן הפעילות והבקשות הפתוחות לשחר.' },
];

function HubCard({ panel, position, onOpen, reorderable, dragged, onDragStart, onDragEnter, onDragEnd }: {
  panel: Panel;
  /** 1-based slot number — drawn only when reordering is available, so "put
      salary at 2" is a sentence both sides of a call can follow. */
  position: number;
  onOpen: (r: string) => void;
  reorderable: boolean;
  dragged: boolean;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDragEnd: () => void;
}) {
  const face = FACE[panel.id] ?? { icon: '▪', tone: 'slate' };
  return (
    <button
      type="button"
      className={`hub-card tone-${face.tone}${dragged ? ' hub-card-dragging' : ''}`}
      onClick={() => onOpen(panel.route)}
      draggable={reorderable}
      onDragStart={reorderable ? onDragStart : undefined}
      onDragEnter={reorderable ? onDragEnter : undefined}
      onDragOver={reorderable ? (e) => e.preventDefault() : undefined}
      onDragEnd={reorderable ? onDragEnd : undefined}
    >
      {reorderable && <span className="hub-card-num" aria-hidden="true">{position}</span>}
      <span className="hub-card-icon" aria-hidden="true">{face.icon}</span>
      <span className="hub-card-body">
        <span className="hub-card-title">
          {panel.name}
          {panel.level === 'edit' && <span className="hub-card-badge">עריכה</span>}
        </span>
        {panel.purpose && <span className="hub-card-text">{panel.purpose}</span>}
      </span>
      <span className="hub-card-go" aria-hidden="true">←</span>
    </button>
  );
}

export default function Hub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loading, panels, role } = usePermissions();
  // Reordering is admin-only for now (Ohad 2026-08-15) — not because it is
  // dangerous (each user could only ever move their own view), but because a
  // trackpad drag that silently rearranges a non-technical user's screen reads
  // as "the dashboard moved on me" and becomes a support call. One line to open up.
  const canReorder = role === 'admin';

  // Local copy of the (already server-sorted) cards, so dragging reorders live.
  const [cards, setCards] = useState<Panel[]>([]);
  const dragId = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Mirror of `cards` for dragEnd to read — saving from inside a setCards updater
  // ran the POST twice under StrictMode (two identical rows per drag in BQ).
  const cardsRef = useRef<Panel[]>([]);
  useEffect(() => { cardsRef.current = cards; }, [cards]);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setCards(panels); }, [panels]);

  const dragEnter = (overId: string) => {
    const from = dragId.current;
    if (!from || from === overId) return;
    setCards((prev) => {
      const next = [...prev];
      const i = next.findIndex((p) => p.id === from);
      const j = next.findIndex((p) => p.id === overId);
      if (i < 0 || j < 0) return prev;
      const [moved] = next.splice(i, 1);
      next.splice(j, 0, moved);
      return next;
    });
  };

  const dragEnd = () => {
    dragId.current = null;
    setDraggingId(null);
    // The "✓ נשמר" flash is the answer to "did it actually save?" — it appears
    // only after the server said yes. A failure just means no flash: the order
    // on screen is right either way, only the next device would miss it.
    const order = cardsRef.current.map((p) => p.id);
    execApi.saveDashboardOrder(order)
      .then(() => {
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2500);
        // Tell every mounted usePermissions (the sidebar) to re-sort right now.
        window.dispatchEvent(new CustomEvent('dashboards:order', { detail: order }));
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
          <Header title="הדשבורדים שלי" />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="hub-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => <div className="hub-card hub-card-sk" key={i} />)}
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Granted nothing at all: say so plainly instead of drawing an empty grid, which
  // reads as a broken page rather than as a permissions answer.
  if (!panels.length) return <AccessDenied reason="grant" />;

  const first = (user?.email || '').split('@')[0];

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        <Header title="הדשבורדים שלי" />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="hub-wrap">
            <div className="hub-intro">
              <h1 className="hub-hello">היי {first} 👋</h1>
              <p className="hub-sub">
                {cards.length === 1 ? 'הדשבורד שפתוח לך' : `${cards.length} דשבורדים פתוחים לך`}
                {role === 'admin' && ' · הרשאת אדמין'}
                {savedFlash && <span className="hub-saved"> · ✓ הסדר נשמר</span>}
              </p>
            </div>
            <div className="hub-grid">
              {cards.map((p, i) => (
                <HubCard
                  key={p.id}
                  panel={p}
                  position={i + 1}
                  onOpen={(r) => navigate(r)}
                  reorderable={canReorder}
                  dragged={draggingId === p.id}
                  onDragStart={() => { dragId.current = p.id; setDraggingId(p.id); }}
                  onDragEnter={() => dragEnter(p.id)}
                  onDragEnd={dragEnd}
                />
              ))}
              {role === 'admin' && ADMIN_CARDS.map((p) => (
                <HubCard
                  key={p.id}
                  panel={p}
                  position={0}
                  onOpen={(r) => navigate(r)}
                  reorderable={false}
                  dragged={false}
                  onDragStart={() => {}}
                  onDragEnter={() => {}}
                  onDragEnd={() => {}}
                />
              ))}
            </div>
            <p className="hub-foot">
              {canReorder && cards.length > 1 && <>אפשר לגרור קובייה כדי לשנות את הסדר · </>}
              חסר לך דשבורד? פנה לאוהד.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
